/**
 * Mahjong — hub at /mahjong. One complete screenful on the tea table.
 *
 * Composition (landscape ≥1024px, per docs/TOUCH-TV-CONTRACT.md):
 *   - LEFT: the masthead (eyebrow / title / one-line lede) and the "Today's
 *     board" hero — one featured layout per day, the same for everyone in a
 *     community, drawn as a large stacked-tile silhouette with two big
 *     affordances (play the daily deal / deal a fresh one).
 *   - RIGHT: "the board cabinet" — all 72 published layouts as a PAGED wall
 *     of silhouette cards (8 per page, stepped paging with big page-turn
 *     buttons; never a free scroll). One tap on a card deals a fresh game on
 *     that board — straight to play, no intermediate screen (playbook #5).
 *
 * Every silhouette is structural geometry: the layout's real slot data drawn
 * as layered rounded rects, so each board's true shape sells the choice.
 * Phone portrait (≤430px) stacks the same pieces and may scroll.
 */
import { motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { usePreferences } from '@/preferences';

import { TableFrame } from './TableFrame';
import { LAYOUTS, type Layout } from './layouts';
import { cardSpring, hoverSpring } from './motion';
import { seedFrom, todayStamp } from './rng';
import { sound } from './sound';

import './Mahjong.css';
import './MahjongHub.css';

/** Boards per page of the cabinet — 4×2 at landscape, 2×4 stacked on phones. */
const PAGE_SIZE = 8;

/** The seven KMahjongg classics lead the cabinet; the rest follow A→Z. */
const CLASSICS: readonly string[] = [
  'turtle',
  'dragon',
  'cat',
  'crab',
  'spider',
  'castle',
  'castle2',
];

const ORDERED_LAYOUTS: readonly Layout[] = (() => {
  const classics = CLASSICS.map((id) => LAYOUTS.find((l) => l.id === id)).filter(
    (l): l is Layout => l !== undefined,
  );
  const rest = LAYOUTS.filter((l) => !CLASSICS.includes(l.id));
  return [...classics, ...rest];
})();

/** The featured board of the day — deterministic, same for the whole community. */
export function featuredLayout(date: Date = new Date()): Layout {
  const index = seedFrom(`mahjong-featured-${todayStamp(date)}`) % LAYOUTS.length;
  return LAYOUTS[index]!;
}

// ---------------------------------------------------------------------------
// Layout silhouette — the board's real slot geometry, drawn small.
// ---------------------------------------------------------------------------

/** Per-layer offset (in half-cell units) — echoes the real board's up-left lift. */
const SIL_DX = -0.3;
const SIL_DY = -0.4;

function LayoutSilhouette({ layout }: { layout: Layout }): React.JSX.Element {
  // Draw table layer first, upper layers over it — same painter's order as
  // the real board.
  const sorted = useMemo(
    () => [...layout.slots].sort((a, b) => a.z - b.z || a.y - b.y || a.x - b.x),
    [layout],
  );
  const liftX = Math.abs(SIL_DX) * layout.maxLayer;
  const liftY = Math.abs(SIL_DY) * layout.maxLayer;
  const pad = 0.6;
  const viewBox = `${-liftX - pad} ${-liftY - pad} ${layout.cols + liftX + pad * 2} ${
    layout.rows + liftY + pad * 2
  }`;
  return (
    <svg
      className="mj-hub__sil"
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
    >
      {sorted.map((s, i) => (
        <rect
          key={i}
          className={`mj-hub__sil-tile mj-hub__sil-z${Math.min(s.z, 4)}`}
          x={s.x + s.z * SIL_DX}
          y={s.y + s.z * SIL_DY}
          width={2}
          height={2}
          rx={0.42}
        />
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// The hub
// ---------------------------------------------------------------------------

export function MahjongHub(): React.JSX.Element {
  const navigate = useNavigate();
  const { theme } = usePreferences();
  const [page, setPage] = useState(0);

  const featured = useMemo(() => featuredLayout(), []);
  const pageCount = Math.ceil(ORDERED_LAYOUTS.length / PAGE_SIZE);
  const pageItems = ORDERED_LAYOUTS.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  useEffect(() => {
    const t = window.setTimeout(() => {
      sound.unlock();
      sound.play('nav');
    }, 80);
    return () => window.clearTimeout(t);
  }, []);

  const go = (layoutId: string, mode: 'daily' | 'practice'): void => {
    sound.unlock();
    sound.play('nav');
    navigate(`/play/mahjong?layout=${layoutId}&mode=${mode}`);
  };

  const flip = (delta: number): void => {
    setPage((p) => Math.min(pageCount - 1, Math.max(0, p + delta)));
    sound.unlock();
    sound.play('clack');
  };

  return (
    <div className="mj mj-hub" data-theme={theme}>
      <TableFrame theme={theme}>
        <div className="mj-hub__layout">
          {/* ---- Left: masthead + today's board ---- */}
          <section className="mj-hub__today" aria-label="Today's board">
            <motion.header
              className="mj-hub__head"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
            >
              <p className="mj-hub__eyebrow">Rendever Games</p>
              <h1 className="mj-hub__title">Mahjong</h1>
              <p className="mj-hub__lede">
                Match free tiles in pairs and clear the table. Every board is solvable — and a
                gentle Shuffle is always there if you need it.
              </p>
            </motion.header>

            <motion.article
              className="mj-hub__hero"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...cardSpring, delay: 0.12 }}
            >
              <div className="mj-hub__hero-top">
                <p className="mj-hub__hero-eyebrow">Today, together</p>
                <h2 className="mj-hub__hero-title">{featured.name}</h2>
                <p className="mj-hub__hero-meta">
                  {featured.tileCount} tiles · {featured.maxLayer + 1}{' '}
                  {featured.maxLayer === 0 ? 'layer' : 'layers'}
                </p>
              </div>

              <div className="mj-hub__hero-sil">
                <LayoutSilhouette layout={featured} />
              </div>

              <p className="mj-hub__hero-blurb">
                The same board everyone in your community is playing today.
              </p>

              <div className="mj-hub__hero-actions">
                <button
                  type="button"
                  className="mj-hub__btn mj-hub__btn--primary"
                  onClick={() => go(featured.id, 'daily')}
                  onPointerEnter={() => sound.play('tap')}
                >
                  Play today&rsquo;s board
                </button>
                <button
                  type="button"
                  className="mj-hub__btn mj-hub__btn--secondary"
                  onClick={() => go(featured.id, 'practice')}
                  onPointerEnter={() => sound.play('tap')}
                >
                  New game on this board
                </button>
              </div>
            </motion.article>
          </section>

          {/* ---- Right: the board cabinet, one page at a time ---- */}
          <section className="mj-hub__browse" aria-label="Choose any board">
            <motion.div
              className="mj-hub__browse-head"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            >
              <h2 className="mj-hub__browse-title">Or pick any board</h2>
              <p className="mj-hub__browse-hint">One tap deals a fresh game.</p>
            </motion.div>

            <div className="mj-hub__wall-frame">
              {/* Re-keyed per page so each page-turn re-runs the settle-in. */}
              <ul className="mj-hub__wall" role="list" key={page}>
                {pageItems.map((layout, i) => (
                  <motion.li
                    key={layout.id}
                    className="mj-hub__board-item"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ ...cardSpring, delay: 0.05 + i * 0.03 }}
                    whileHover={{ y: -4, transition: hoverSpring }}
                  >
                    <button
                      type="button"
                      className="mj-hub__board"
                      onClick={() => go(layout.id, 'practice')}
                      onPointerEnter={() => sound.play('tap')}
                      aria-label={`Play ${layout.name} — ${layout.tileCount} tiles`}
                    >
                      {layout.id === featured.id && (
                        <span className="mj-hub__board-badge">Today</span>
                      )}
                      <span className="mj-hub__board-sil">
                        <LayoutSilhouette layout={layout} />
                      </span>
                      <span className="mj-hub__board-name">{layout.name}</span>
                      <span className="mj-hub__board-meta">{layout.tileCount} tiles</span>
                    </button>
                  </motion.li>
                ))}
              </ul>
            </div>

            <nav className="mj-hub__pager" aria-label="More boards">
              <button
                type="button"
                className="mj-hub__page-btn"
                onClick={() => flip(-1)}
                disabled={page === 0}
                aria-label="Previous boards"
              >
                <span aria-hidden="true">‹</span>
              </button>
              <span className="mj-hub__page-label" aria-live="polite">
                Page {page + 1} of {pageCount}
              </span>
              <button
                type="button"
                className="mj-hub__page-btn"
                onClick={() => flip(1)}
                disabled={page === pageCount - 1}
                aria-label="Next boards"
              >
                <span aria-hidden="true">›</span>
              </button>
            </nav>
          </section>
        </div>
      </TableFrame>
    </div>
  );
}
